import { DataSource } from '@angular/cdk/collections';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { ListOfFirmwarePackage } from '@ottobock/remote-update/domain';
import { merge, Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';

import { compareFirmwareVersions } from '../../sortFirmwareVersions';

/**
 * Data source for the FirmwareTable view. This class should
 * encapsulate all logic for fetching and manipulating the displayed data
 * (including sorting, pagination, and filtering).
 */
export class FirmwareTableDataSource extends DataSource<ListOfFirmwarePackage> {
  data: ListOfFirmwarePackage[] = [];
  paginator: MatPaginator | undefined;
  sort: MatSort | undefined;

  constructor(items: ListOfFirmwarePackage[]) {
    super();
    this.data.push(...items);
  }

  setFirmwarePackageData(data: ListOfFirmwarePackage[]) {
    this.data = [...data];
  }
  /**
   * Connect this data source to the table. The table will only update when
   * the returned stream emits new items.
   * @returns A stream of the items to be rendered.
   */

  connect(): Observable<ListOfFirmwarePackage[]> {
    if (!this.paginator || !this.sort) {
      throw Error('Paginator and sort must be set before connecting.');
    }

    // Merge sortChange and paginator.page, but emit once initially
    return merge(this.sort.sortChange, this.paginator.page).pipe(
      startWith(null), // triggers initial sort and pagination
      map(() => {
        const sortedData = this.getSortedData([...this.data]);
        return this.getPagedData(sortedData);
      })
    );
  }

  /**
   *  Called when the table is being destroyed. Use this function, to clean up
   * any open connections or free any held resources that were set up during connect.
   */
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  disconnect(): void {}

  /**
   * Paginate the data (client-side). If you're using server-side pagination,
   * this would be replaced by requesting the appropriate data from the server.
   */
  private getPagedData(data: ListOfFirmwarePackage[]): ListOfFirmwarePackage[] {
    if (this.paginator) {
      const startIndex = this.paginator.pageIndex * this.paginator.pageSize;
      return data.splice(startIndex, this.paginator.pageSize);
    } else {
      return data;
    }
  }

  /**
   * Sort the data (client-side). If you're using server-side sorting,
   * this would be replaced by requesting the appropriate data from the server.
   */
  private getSortedData(data: ListOfFirmwarePackage[]): ListOfFirmwarePackage[] {
    if (!this.sort || !this.sort.active || this.sort.direction === '') {
      return data;
    }

    return data.sort((a, b) => {
      const isAsc = this.sort?.direction === 'asc';
      switch (this.sort?.active) {
        case 'bundle_version':
          return compareFirmwareVersions(a.version, b.version, isAsc);
        case 'state':
          return compareFimwareStates(a.state, b.state, isAsc);
        case 'description':
          return compare(a.description, b.description, isAsc);
        case 'used_in_rollout_plans':
          return compare(+a.numberOfRollouts, +b.numberOfRollouts, isAsc);
        case 'updated_at':
          return compare(new Date(a.updatedAt).getTime(), new Date(b.updatedAt).getTime(), isAsc);
        default:
          return 0;
      }
    });
  }
}

/** Simple sort comparator for example ID/Name columns (for client-side sorting). */
function compare(a: string | number, b: string | number, isAsc: boolean): number {
  if (a === b) return 0;
  return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
}

const stateOrder: Record<string, number> = {
  Draft: 1,
  Released: 2,
  Withdrawn: 3
};

function compareFimwareStates(a: string, b: string, isAsc: boolean): number {
  const orderA = stateOrder[a];
  const orderB = stateOrder[b];

  const diff = orderA - orderB;
  return isAsc ? diff : -diff;
}
